import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import React, { useCallback } from 'react';
import {
  MultiDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { withLocalePrefix } from 'shared/utils/translation';
import Api from '../../lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { PageName } from '../../shared/types';

type Props = {
  apiClient: Api;
  pageName: PageName;
};

const TagsDropdown = ({ apiClient, pageName }: Props) => {
  const queryClient = useQueryClient();
  const { watch, setValue } = useFormContext();
  const candidateTags: Item[] = watch('candidate_tags');

  const t = withLocalePrefix(`talent_${pageName}.labels`);

  const fetchTags = useCallback(
    async (term: string) => {
      const queryKey = ['candidateTags', term];

      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);

      if (cachedData) {
        return { items: cachedData };
      }

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => apiClient.fetchTags(term),
      });
      return { items };
    },
    [apiClient, queryClient]
  );

  const onSelectedTagsChange = (e: { selectedItems: Array<Item> }) => {
    setValue('candidate_tags', e.selectedItems, { shouldDirty: true });
  };

  const tagsDropdownProps = useMultiDropdown({
    fetchItems: fetchTags,
    onSelectedItemsChange: onSelectedTagsChange,
    selectedItems: candidateTags,
    preventLoadOnMount: true,
  });

  return (
    <MultiDropdown dropdownProps={tagsDropdownProps}>
      <MultiDropdown.Field
        data-provides={`talent-${pageName}-tags-dropdown`}
        label={t('tags')}
      />
      <MultiDropdown.Flyout />
    </MultiDropdown>
  );
};

export default TagsDropdown;
